import db from './db';
import { rootDomainUrl } from './meta';

/**
 * Build the share URL for an agent chat (uses the web app host, not workspace subdomain)
 * @param {Object} agentChat - Agent chat object with share_key property
 * @returns {string} The agent chat share URL
 */
export const buildAgentChatShareUrl = (agentChat) => {
  return rootDomainUrl(`/agent-chat/${agentChat.share_key}`);
};

const WIDGET_CHAT_KIND = 'widget';

/**
 * A request message waiting in a widget chat's queue. Not part of the transcript, not a
 * turn boundary, and never a stop target until the server promotes it.
 * @param {Object} message
 * @returns {boolean}
 */
export const isQueuedRequestMessage = (message) => {
  return message?.kind === 'request' && message?.status === 'queued';
};

/**
 * A widget chat that hasn't been submitted yet. It's created locally so the composer has
 * somewhere to post to, and the API deliberately leaves it out of the chat list until the
 * first submission — so cache prunes driven by that list must leave it alone.
 * @param {Object} agentChat
 * @returns {boolean}
 */
export const isUnstartedWidgetChat = (agentChat) => {
  return agentChat?.kind === WIDGET_CHAT_KIND && agentChat.status === 'pending';
};

/**
 * Widget chats deliberately absent from the list API but still needed by the open
 * Studio surface: either a new chat awaiting its first request, or a workspace chat
 * the viewer opened directly but has not participated in yet.
 * @param {Object} agentChat
 * @returns {boolean}
 */
export const isLocallyRetainedWidgetChat = (agentChat) => {
  return isUnstartedWidgetChat(agentChat) || (agentChat?.kind === WIDGET_CHAT_KIND && agentChat.studio_sidebar_visible === false);
};

/**
 * A widget chat that belongs in this viewer's Studio sidebar. A direct-opened workspace
 * widget stays out until the viewer submits a retained request of their own.
 * @param {Object} agentChat
 * @returns {boolean}
 */
export const isStudioSidebarWidgetChat = (agentChat) => {
  return (
    Boolean(agentChat?.id) &&
    agentChat.kind === WIDGET_CHAT_KIND &&
    agentChat.status !== 'pending' &&
    agentChat.studio_sidebar_visible === true
  );
};

/**
 * The workspace's widget chats, for live queries. Deliberately observes the kind index —
 * agent_chats has no [workspace_id+kind] index, and observing the workspace range would
 * rerun the query on every standard-chat journal write.
 * @param {string} workspaceId
 * @returns {Promise<Array>}
 */
export const workspaceWidgetChats = async (workspaceId) => {
  const widgetChats = await db.agent_chats.where('kind').equals(WIDGET_CHAT_KIND).toArray();
  return widgetChats.filter((agentChat) => agentChat.workspace_id === workspaceId);
};
